// The Review chip (New / Updated / Split / Approved / No WMS; hover → APPROVE), History's Finished
// chip, the Routing window chip, whose tooltip says which pick ticket set each date (the
// strictest wins), and the "Pre-routed" marker of a line a pre-route moved onto.

import { createContext, useContext } from "react";
import { CheckIcon, LoaderCircleIcon } from "lucide-react";
import {
  REVIEW_LABELS,
  dayOfWeek,
  formatDay,
  preRouteMarkerText,
  ruleSourceText,
  skippedText,
  ruleTurnedOff,
  windowShort,
} from "@app/shared";
import type { FinishedStatus, LineView, PreRouteRecord, Review, Row, WindowSetter, WindowState } from "@app/shared";
import { Tooltip, TooltipContent, TooltipTrigger } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { finishedBadge } from "./columns";

/** History's tab colours: Shipped teal, Cancelled red, Regrouped by WMS slate. */
const FINISHED_TONE: Record<FinishedStatus, string> = {
  Shipped: "bg-teal-50 text-teal-800 ring-teal-300",
  Cancelled: "bg-red-50 text-red-700 ring-red-300",
  "Regrouped by WMS": "bg-slate-100 text-slate-600 ring-slate-300",
};
const FINISHED_DOT: Record<FinishedStatus, string> = {
  Shipped: "bg-teal-600",
  Cancelled: "bg-red-600",
  "Regrouped by WMS": "bg-slate-400",
};

/**
 * How a History line finished, in its narrow pinned column: the date alone in the status's colour
 * ("09/19"; a dot before it), "Regrouped" for one regrouped by WMS (`finishedBadge`); the whole
 * ("Shipped 09/19/26") on hover and in the line details.
 */
export function FinishedChip({ finished }: { finished: LineView["finished"] }) {
  if (!finished) return <span className="text-muted-foreground px-2">—</span>;
  const badge = finishedBadge(finished);
  return (
    <span
      title={badge.full}
      aria-label={badge.full}
      className={cn(
        "mx-1 inline-flex h-6 max-w-[calc(100%-0.5rem)] items-center gap-1 truncate rounded px-1.5 text-[11px] font-semibold tabular-nums whitespace-nowrap ring-1 ring-inset",
        FINISHED_TONE[finished.status],
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", FINISHED_DOT[finished.status])} aria-hidden />
      {badge.text}
    </span>
  );
}

export function ReviewChip({
  review,
  active,
  approving,
  onApprove,
}: {
  review: Review;
  active: boolean;
  /** Set while the line is being approved: no second start, the pick tickets done so far. */
  approving?: { done: number; total: number };
  onApprove: () => void;
}) {
  if (approving) {
    return (
      <span
        className="mx-1 inline-flex h-6 min-w-[4.5rem] items-center justify-center gap-1 rounded bg-emerald-50 px-2 text-[11px] font-semibold text-emerald-800 tabular-nums ring-1 ring-emerald-300 ring-inset"
        title={`Approving: ${approving.done} of ${approving.total} pick tickets saved`}
      >
        <LoaderCircleIcon className="size-3 animate-spin" />
        {/* Room for the count only (the column is 84): the words are on hover. */}
        {`${approving.done}/${approving.total}`}
      </span>
    );
  }
  if (review === "approved") {
    return (
      <Tooltip>
        <TooltipTrigger
          render={<span className="flex items-center gap-1 px-1.5 text-xs font-medium text-emerald-700" />}
        >
          <CheckIcon className="size-3.5" /> Approved
        </TooltipTrigger>
        <TooltipContent>Reviewed by the warehouse</TooltipContent>
      </Tooltip>
    );
  }
  if (review === "nowms") {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span className="mx-1 inline-flex h-6 min-w-[4.5rem] items-center justify-center rounded bg-stone-100 px-2 text-[11px] font-bold tracking-wide text-stone-600 ring-1 ring-stone-300 ring-inset" />
          }
        >
          No WMS
        </TooltipTrigger>
        <TooltipContent>No WMS # in A2000 yet: read-only until it gets one, then it joins that WMS line</TooltipContent>
      </Tooltip>
    );
  }
  const TONE: Record<"new" | "updated" | "split", string> = {
    new: "bg-blue-600 text-white ring-blue-700",
    updated: "bg-amber-100 text-amber-800 ring-amber-400",
    split: "bg-violet-100 text-violet-800 ring-violet-400",
  };
  const TIP: Record<"new" | "updated" | "split", string> = {
    new: "A WMS # the app had not seen — click to approve",
    updated: "Changed in A2000 or Deposco after it was approved (the marked cells) — click to approve the change",
    split:
      "Its pick tickets came from other WMS lines, with their entries (not Scheduled Ship Date) — click to approve",
  };
  const chip = cn(
    "group/chip mx-1 inline-flex h-6 min-w-[4.5rem] items-center justify-center rounded px-2 text-[11px] font-bold tracking-wide ring-1 ring-inset",
    TONE[review],
    active && "cursor-pointer hover:bg-emerald-600 hover:text-white hover:ring-emerald-700",
  );
  return (
    <Tooltip>
      <TooltipTrigger
        render={active ? <button type="button" className={chip} onClick={onApprove} /> : <span className={chip} />}
      >
        <span className={cn(active && "group-hover/chip:hidden")}>{REVIEW_LABELS[review]}</span>
        {active ? <span className="hidden group-hover/chip:inline">APPROVE</span> : null}
      </TooltipTrigger>
      <TooltipContent>{TIP[review]}</TooltipContent>
    </Tooltip>
  );
}

const WINDOW_TONE: Record<WindowState, string> = {
  overdue: "bg-red-50 text-red-700 ring-red-200 [--dot:var(--color-red-600)]",
  today: "bg-orange-50 text-orange-700 ring-orange-200 [--dot:var(--color-orange-600)]",
  tomorrow: "bg-orange-50 text-orange-700 ring-orange-200 [--dot:var(--color-orange-600)]",
  due: "bg-amber-50 text-amber-700 ring-amber-200 [--dot:var(--color-amber-500)]",
  open: "bg-slate-100 text-slate-600 ring-slate-200 [--dot:var(--color-slate-400)]",
  requested: "bg-blue-50 text-blue-700 ring-blue-200 [--dot:var(--color-blue-600)]",
  notyet: "bg-slate-50 text-slate-500 ring-slate-200 [--dot:var(--color-slate-300)]",
  norule: "bg-slate-50 text-slate-500 ring-slate-200 ring-dashed [--dot:var(--color-slate-300)]",
  routed: "bg-emerald-50 text-emerald-700 ring-emerald-200 [--dot:var(--color-emerald-600)]",
  hold: "bg-violet-50 text-violet-700 ring-violet-200 [--dot:var(--color-violet-600)]",
};

const when = (d: string) => `${dayOfWeek(d)} ${formatDay(d)}`;

/**
 * The Routing window cell: a dot and the short text (`windowShort`: "Open · 11/06", "Route now"),
 * the whole sentence ("Window open · 3d left") and its dates on hover.
 */
export function WindowChip({ row }: { row: Row }) {
  const w = row.window;
  const short = windowShort(w, w.state === "norule" && ruleTurnedOff(row.line.pts));
  const several = row.line.pts.filter((p) => !p.gone).length > 1;
  const left =
    w.left === null
      ? ""
      : w.opens && w.state === "notyet"
        ? "Not open yet"
        : w.left < 0
          ? `${-w.left} business day${w.left === -1 ? "" : "s"} late`
          : w.left === 0
            ? "Last day is today"
            : `${w.left} business day${w.left === 1 ? "" : "s"} left`;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              "mx-1 inline-flex h-6 max-w-[calc(100%-0.5rem)] items-center gap-1 truncate rounded-full px-1.5 text-xs font-semibold ring-1 ring-inset",
              WINDOW_TONE[w.state],
            )}
          />
        }
      >
        <span className="size-1.5 shrink-0 rounded-full bg-(--dot)" />
        <span className="truncate">{short}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="font-semibold" data-testid="window-label">
          {short === "Turned off" ? "No routing rule · turned off in the app" : w.label}
        </p>
        {w.state === "norule" ? (
          <p>
            No routing rule: no customer of this line has Window opens — no app rule (Admin → Routing rules) and no
            CUST_REF1 in A2000 — or none has a first ship date, so the window can't be worked out.
          </p>
        ) : (
          <div className="space-y-0.5">
            {w.opens ? (
              <p>
                Opens {w.openBdays} business days before first ship → {when(w.opens)}
                {w.opensBy ? <SetBy by={w.opensBy} several={several} /> : null}
              </p>
            ) : null}
            {w.routeBy ? (
              <p>
                Route by {w.dueBdays} business days before cancel → {when(w.routeBy)}
                {w.routeBySetBy ? <SetBy by={w.routeBySetBy} several={several} /> : null}
              </p>
            ) : (
              <p>No deadline: no Route by for any customer of this line — no app rule, no CUST_REF2 in A2000.</p>
            )}
            {several ? (
              <p className="text-muted-foreground">The earliest of the line's pick tickets: the strictest wins.</p>
            ) : null}
            {w.skipped.length ? <p>Skips {skippedText(w.skipped)}</p> : null}
            {left ? <p>{left} · weekends and holidays excluded</p> : null}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/** Where a date's rule came from ("Set in the app, set by …", "From A2000 (this customer)": the words of Admin → Routing rules), and on a line of several the pick ticket (and its customer) that set it. */
function SetBy({ by, several }: { by: WindowSetter; several: boolean }) {
  return (
    <span className="text-muted-foreground">
      {by.rule ? ` (${ruleSourceText(by.rule)})` : null}
      {several ? ` · PT ${by.pickTicket}, ${by.customer}` : null}
    </span>
  );
}

/** The pre-route each stored line came from (`preRouteOfLine`), given once by the page for every cell. */
export const PreRouteMarks = createContext<ReadonlyMap<string, PreRouteRecord>>(new Map());

/** "Pre-routed", on a line a pre-route moved onto; who added it and when on hover. */
export function PreRoutedMarker({ line }: { line: LineView }) {
  const marks = useContext(PreRouteMarks);
  const from = line.stored ? marks.get(line.stored.id) : undefined;
  if (!from) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            data-testid="pre-routed"
            className="shrink-0 rounded bg-indigo-50 px-1 py-px text-[10px] font-semibold text-indigo-700 ring-1 ring-indigo-200"
          />
        }
      >
        Pre-routed
      </TooltipTrigger>
      <TooltipContent>{preRouteMarkerText(from)}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A line an action is saving (item 1): a spinner where its checkbox was, "Saving…" on hover; still
 * under reduced motion, never colour alone. The row's green / red marker is `lineStateClass`.
 */
export function SavingMark() {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="status"
            aria-label="Saving…"
            className="flex size-4 items-center justify-center text-emerald-700"
          />
        }
      >
        <LoaderCircleIcon className="size-4 motion-safe:animate-spin" aria-hidden />
      </TooltipTrigger>
      <TooltipContent>Saving…</TooltipContent>
    </Tooltip>
  );
}

/**
 * The row's marker for its line's save: green while saving, fading once saved (a cell's own save,
 * `edited`: longer, so the person sees where the line is), red when it failed.
 */
export const lineStateClass = (state: "saving" | "saved" | "edited" | "failed" | undefined) =>
  state === "saving"
    ? "line-saving"
    : state === "saved"
      ? "line-saved"
      : state === "edited"
        ? "line-saved line-edited"
        : state === "failed"
          ? "line-failed"
          : "";
